import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Invoices.css';

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

function PurchaseModal({ products, onSave, onClose }) {
  const navigate = useNavigate();
  const [supplierName, setSupplierName] = useState('');
  const [supplierPhone, setSupplierPhone] = useState('');
  const [supplierGSTIN, setSupplierGSTIN] = useState('');
  const [supplierState, setSupplierState] = useState('Karnataka');
  const [items, setItems] = useState([]);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [notes, setNotes] = useState('');

  const [currentItem, setCurrentItem] = useState({
    productId: '',
    quantity: 1,
    purchasePrice: 0,
    taxRate: 0,
  });

  const handleAddItem = () => {
    if (!currentItem.productId || toNumber(currentItem.quantity) <= 0) {
      alert('Please select a product and enter quantity');
      return;
    }

    const product = products.find((p) => p.id === currentItem.productId);
    if (!product) return;

    const quantity = toNumber(currentItem.quantity);
    const purchasePrice = toNumber(currentItem.purchasePrice, toNumber(product.purchasePrice, 0));
    const itemTaxRate = toNumber(currentItem.taxRate, toNumber(product.taxRate, 0));
    const subtotal = purchasePrice * quantity;

    let cgst = 0, sgst = 0, igst = 0;
    
    if (itemTaxRate > 0) {
      const taxAmount = (subtotal * itemTaxRate) / 100;
      cgst = taxAmount / 2;
      sgst = taxAmount / 2;
    }

    const newItem = {
      productId: product.id,
      productName: product.productName,
      hsnCode: product.hsnCode,
      quantity,
      purchasePrice: purchasePrice,
      taxRate: itemTaxRate,
      cgst,
      sgst,
      igst,
      itemTotal: subtotal + cgst + sgst + igst,
    };

    setItems([...items, newItem]);
    setCurrentItem({ productId: '', quantity: 1, purchasePrice: 0, taxRate: 0 });
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;

    items.forEach((item) => {
      subtotal += item.purchasePrice * item.quantity;
      totalTax += item.cgst + item.sgst + item.igst;
    });

    return { subtotal, totalTax };
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!supplierName || items.length === 0) {
      alert('Please enter supplier name and add items');
      return;
    }

    const { subtotal, totalTax } = calculateTotals();
    const grandTotal = subtotal + totalTax;

    const purchaseData = {
      supplierName,
      supplierPhone,
      supplierGSTIN,
      supplierState,
      items,
      subtotal,
      totalTax,
      grandTotal,
      paymentMode,
      notes,
    };

    onSave(purchaseData);
  };

  const { subtotal, totalTax } = calculateTotals();
  const grandTotal = subtotal + totalTax;

  return (
    <div className="modal-overlay">
      <div className="modal invoice-modal-with-preview">
        <div className="modal-header">
          <h2>Add Purchase Entry</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="invoice-container">
          <div className="invoice-form-section">
            <form onSubmit={handleSubmit} className="invoice-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Supplier Name *</label>
                  <input
                    type="text"
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Supplier Phone</label>
                  <input
                    type="text"
                    value={supplierPhone}
                    onChange={(e) => setSupplierPhone(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Supplier GSTIN</label>
                  <input
                    type="text"
                    value={supplierGSTIN}
                    onChange={(e) => setSupplierGSTIN(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>State</label>
                  <select value={supplierState} onChange={(e) => setSupplierState(e.target.value)}>
                    <option>Karnataka</option>
                    <option>Tamil Nadu</option>
                    <option>Telangana</option>
                    <option>Andhra Pradesh</option>
                    <option>Maharashtra</option>
                    <option>Gujarat</option>
                    <option>Madhya Pradesh</option>
                    <option>Rajasthan</option>
                    <option>Uttar Pradesh</option>
                    <option>Delhi</option>
                    <option>Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Payment Mode</label>
                  <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                    <option>Cash</option>
                    <option>UPI</option>
                    <option>Card</option>
                    <option>Credit</option>
                    <option>Bank Transfer</option>
                  </select>
                </div>
              </div>

              <div className="add-item-section">
                <h3>Add Items</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Product</label>
                    <select
                      value={currentItem.productId}
                      onChange={(e) => {
                        const product = products.find(p => p.id === e.target.value);
                        setCurrentItem({ 
                          ...currentItem, 
                          productId: e.target.value,
                          purchasePrice: product?.purchasePrice || 0,
                          taxRate: product?.taxRate || 0,
                        });
                      }}
                    >
                      <option value="">-- Select Product --</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.productName} - ₹{p.purchasePrice || p.sellingPrice}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate('/products')}
                    title="Add a new product"
                  >
                    + Add Product
                  </button>

                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      value={currentItem.quantity}
                      onChange={(e) => setCurrentItem({ ...currentItem, quantity: toNumber(e.target.value, 0) })}
                      onWheel={(e) => e.currentTarget.blur()}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>Purchase Price</label>
                    <input
                      type="number"
                      value={currentItem.purchasePrice}
                      onChange={(e) => setCurrentItem({ ...currentItem, purchasePrice: toNumber(e.target.value, 0) })}
                      onWheel={(e) => e.currentTarget.blur()}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>GST %</label>
                    <input
                      type="number"
                      value={currentItem.taxRate}
                      onChange={(e) => setCurrentItem({ ...currentItem, taxRate: toNumber(e.target.value, 0) })}
                      onWheel={(e) => e.currentTarget.blur()}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <button type="button" className="btn btn-primary" onClick={handleAddItem}>
                    Add Item
                  </button>
                </div>
              </div>

              {items.length > 0 && (
                <div className="items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Purchase Price</th>
                        <th>Tax</th>
                        <th>Amount</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.productName}</td>
                          <td>{item.quantity}</td>
                          <td>₹{item.purchasePrice.toFixed(2)}</td>
                          <td>{item.taxRate}%</td>
                          <td>₹{item.itemTotal.toFixed(2)}</td>
                          <td>
                            <button
                              type="button"
                              className="btn-icon delete"
                              onClick={() => handleRemoveItem(idx)}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="invoice-summary">
                <div>
                  <label>Subtotal</label>
                  <p>₹{subtotal.toFixed(2)}</p>
                </div>
                <div>
                  <label>Total Tax</label>
                  <p>₹{totalTax.toFixed(2)}</p>
                </div>
                <div className="grand-total">
                  <label>Grand Total</label>
                  <p>₹{grandTotal.toFixed(2)}</p>
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows="2"
                  placeholder="Additional notes..."
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Purchase
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PurchaseModal;
